import Config from '@/utils/config'
import { useUserStore } from '@/stores/UserStore'
import type { Router } from 'vue-router'

// --- per-app block ----------------------------------------------------
// The only part that differs between the four copies of this file. Everything
// below it is identical; diff the copies before changing anything past here.

export const SOURCE = 'instore'

/** Web only — soundz-app is the copy that also ships native and desktop. */
const platform = () => 'web'

/** Always same-origin: /e on this app's own SSR server proxies to the backend. */
const endpoint = () => '/e'

/** Same alphabet and length as UuidTrait::generateId() in soundz-backend. */
function generateRandomString(length: number) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) result += chars.charAt(Math.floor(Math.random() * chars.length))
  return result
}

// --- end per-app block ------------------------------------------------------

/**
 * First-party event tracking.
 *
 * Deliberately does not go through ApiService: that appends `lang` to every
 * request, and the backend's global Locale middleware resolves the Sanctum user
 * and can write to `users` when it sees one — a user-table write per analytics
 * batch. Raw fetch only.
 *
 * On web the batch is posted same-origin to `/e`, which the SSR server forwards
 * to the backend. Same-origin means no CORS preflight, and a preflight fired
 * during pagehide is exactly the request browsers drop — which is when the last
 * batch of a visit is sent. It also keeps the path off generic ad-block filter
 * lists. Native has neither problem and posts to the API directly.
 *
 * Copy-pasted into the other frontends; keep the copies in step.
 */

/** Mirrors the client-reportable half of App\Enums\EventName in soundz-backend. */
export const EventName = {
  pageView: 'page.view',
  appOpen: 'app.open',
  playerPlay: 'player.play',
  playerPause: 'player.pause',
  playerStop: 'player.stop',
  playerChannelChange: 'player.channel_change',
  playerError: 'player.error',
  playerReconnect: 'player.reconnect',
  playerSleepTimerSet: 'player.sleep_timer_set',
  playerOutputPickerOpen: 'player.output_picker_open',
  onboardingStep: 'onboarding.step',
  onboardingComplete: 'onboarding.complete',
  onboardingSkip: 'onboarding.skip',
  authRegisterSubmit: 'auth.register_submit',
  authOtpSubmit: 'auth.otp_submit',
  authOtpFailed: 'auth.otp_failed',
  pushPermissionPrompt: 'push.permission_prompt',
  pushPermissionGranted: 'push.permission_granted',
  pushPermissionDenied: 'push.permission_denied',
  shareCreated: 'share.created',
  podcastSearch: 'podcast.search',
  hitPreviewPlay: 'hit.preview_play',
  appStoreClick: 'app.store_click',
} as const

export type EventNameValue = typeof EventName[keyof typeof EventName]

export interface TrackProps {
  /**
   * When it happened, if that is not now — a native incident drained from the
   * plugin after the WebView was suspended. Older than the backend's
   * max_event_age_hours is rejected on ingest, so drop those rather than send
   * them.
   */
  occurred_at?: string
  route?: string
  channel_id?: string
  track_id?: string
  show_id?: string
  stream_id?: string
  value?: number
  properties?: Record<string, unknown>
}

/**
 * Where a visit came from. Mirrors the columns on `event_sessions` and the list
 * in `AttributionUtil` in soundz-backend — a name added on one side and not the
 * other is silently dropped on ingest.
 */
interface Campaign {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  click_id?: string
  click_source?: string
}

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

/**
 * Auto-tagged click ids, in the order they win when a URL carries more than one.
 *
 * These matter more than the UTMs do: Google and Meta append a click id and
 * nothing else unless somebody remembers to tag the destination by hand, so
 * without this list a visit that cost money is indistinguishable from an
 * organic search result.
 */
const CLICK_PARAMS = [
  'gclid', 'gbraid', 'wbraid', 'dclid', 'fbclid',
  'msclkid', 'ttclid', 'twclid', 'li_fat_id', 'epik', 'sccid',
]

const UTM_MAX = 128
const CLICK_ID_MAX = 255

const DEVICE_KEY = 'sz_did'
const SESSION_KEY = 'sz_ses'
// The campaign the current session was attributed to. Persisted so a reload of
// the same tagged URL is recognised as the same arrival rather than a new one.
const CAMPAIGN_KEY = 'sz_camp'
const SESSION_IDLE_MS = 30 * 60 * 1000
const FLUSH_INTERVAL_MS = 5000
// Kept under the backend's max_events_per_batch.
const MAX_BATCH = 20

interface QueuedEvent extends TrackProps {
  id: string
  name: EventNameValue
  occurred_at: string
}

let queue: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | undefined
let clockSkewSeconds: number | undefined
let started = false
let campaign: Campaign = {}

/**
 * Variants handed down by the server at bootstrap. Assignment is server-side
 * and first-write-wins, so this is only ever an echo — the client never decides
 * which arm it is in.
 *
 * Events are not stamped with the variant: assignment is per device and lives
 * in experiment_assignments, so analysis joins on device_id. A stamp would only
 * add a way for the two to disagree.
 */
let assignments: Record<string, string> = {}

export function setExperiments(values: Record<string, string> | undefined) {
  assignments = values ?? {}
}

export function variant(experiment: string): string | undefined {
  return assignments[experiment]
}

// __SZ_OPTOUT__ is set by the SSR server when the sz_optout cookie is present:
// staff use these sites all day and would otherwise dominate every number.
const enabled = () =>
  !Config.ssr
  && typeof window !== 'undefined'
  && !(window as unknown as { __SZ_OPTOUT__?: boolean }).__SZ_OPTOUT__

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private mode, or storage disabled.
    return null
  }
}

function writeStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Nothing to do — the visit is simply not stitched to the next one.
  }
}

/**
 * On web the id is minted by the SSR server and handed over as __SZ_DID__: a
 * cookie set in an HTTP response survives Safari's 7-day cap on script-written
 * storage, which localStorage does not. Native has no SSR and no cookie, so it
 * keeps its own, minted in the same 24-char shape.
 */
export function deviceId(): string {
  const injected = (window as unknown as { __SZ_DID__?: string }).__SZ_DID__
  if (injected) return injected

  const stored = readStorage(DEVICE_KEY)
  if (stored) return stored

  const minted = generateRandomString(24)
  writeStorage(DEVICE_KEY, minted)
  return minted
}

/**
 * localStorage rather than sessionStorage, which would mint a separate session
 * per tab. Rolls over after 30 minutes of inactivity.
 *
 * This is a burst of foreground UI activity, not a listening session: on native
 * the WebView is suspended while playback continues, so a long listen spans
 * several of these. Listening duration comes from stream_metrics instead.
 */
function sessionId(): string {
  const now = Date.now()
  const raw = readStorage(SESSION_KEY)

  if (raw) {
    try {
      const { id, seen } = JSON.parse(raw)
      if (id && now - seen < SESSION_IDLE_MS) {
        writeStorage(SESSION_KEY, JSON.stringify({ id, seen: now }))
        return id
      }
    } catch {
      // Fall through and mint a new one.
    }
  }

  const id = generateRandomString(24)
  writeStorage(SESSION_KEY, JSON.stringify({ id, seen: now }))
  return id
}

/** Start a new session immediately, whatever the idle timer thinks. */
function rotateSession() {
  // The queue belongs to the visit that is ending, so it goes out under the old
  // id before the new one exists.
  flush()
  writeStorage(SESSION_KEY, JSON.stringify({ id: generateRandomString(24), seen: Date.now() }))
}

function readCampaign(search: string): Campaign {
  const params = new URLSearchParams(search)
  const found: Campaign = {}

  for (const key of UTM_PARAMS) {
    const value = params.get(key)?.trim()
    if (value) found[key] = value.slice(0, UTM_MAX)
  }

  for (const key of CLICK_PARAMS) {
    const value = params.get(key)?.trim()
    if (value) {
      found.click_id = value.slice(0, CLICK_ID_MAX)
      found.click_source = key
      break
    }
  }

  return found
}

/** Stable identity of a campaign, and '' when there is none. */
function campaignKey(found: Campaign): string {
  const parts = [...UTM_PARAMS, 'click_id' as const].map((key) => found[key] ?? '')

  return parts.some(Boolean) ? parts.join('|') : ''
}

/**
 * The referrer of the request that loaded the page.
 *
 * `__SZ_LANDING__` is what the SSR server saw in the `Referer` header, which is
 * the better answer: `document.referrer` is empty for app-webview and messenger
 * referrals, and current referrer policies cut it down to a bare origin.
 */
function landingReferer(): string | undefined {
  const injected = (window as unknown as { __SZ_LANDING__?: { referer?: string } }).__SZ_LANDING__

  return injected?.referer || document.referrer || undefined
}

/**
 * Attribute this arrival, rotating the session when it is a new one.
 *
 * Without the rotation a campaign that arrives mid-visit is lost outright: the
 * session id survives half an hour of idling and the backend writes a session's
 * attribution exactly once, so an ad clicked ten minutes after an organic visit
 * would be attributed to nothing at all.
 *
 * `url` is for arrivals that are not a page load — a deep link handed to a
 * native app, where there is no navigation to read the campaign off.
 */
export function noteLanding(url?: string) {
  if (!enabled()) return

  let search: string
  try {
    search = url ? new URL(url).search : window.location.search
  } catch {
    return
  }

  const found = readCampaign(search)
  const key = campaignKey(found)
  if (!key) return

  campaign = found

  if (key === readStorage(CAMPAIGN_KEY)) return

  writeStorage(CAMPAIGN_KEY, key)
  rotateSession()
}

function payload() {
  return {
    session: {
      id: sessionId(),
      source: SOURCE,
      platform: platform(),
      app_version: Config.appVersion,
      referer: landingReferer(),
      accept_language: navigator.language?.slice(0, 16),
      client_clock_skew_s: clockSkewSeconds,
      ...campaign,
    },
    device_id: deviceId(),
    events: queue,
  }
}

function send(body: string, beacon: boolean) {
  const url = endpoint()

  // sendBeacon survives the page going away, which fetch does not reliably do
  // even with keepalive. text/plain keeps it a simple request; the proxy
  // normalises the content type on the way to the backend. It cannot carry
  // headers, so an unload batch arrives without the bearer token and is
  // attributed to a user through its session row instead.
  if (beacon && navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain' }))) return

  const token = useUserStore().token
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  void fetch(url, {
    method: 'POST',
    headers,
    body,
    keepalive: true,
    credentials: 'omit',
  })
    .then((response) => {
      const date = response.headers.get('Date')
      if (date) clockSkewSeconds = Math.round((Date.now() - new Date(date).getTime()) / 1000)
    })
    // An analytics outage must never surface to a listener.
    .catch(() => {})
}

export function flush(beacon = false) {
  if (!enabled() || queue.length === 0) return

  const body = JSON.stringify(payload())
  queue = []
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = undefined
  }

  send(body, beacon)
}

export function track(name: EventNameValue, props: TrackProps = {}) {
  if (!enabled()) return

  queue.push({
    // Client-generated, and the backend's idempotency key: visibilitychange
    // fires on every tab switch and app background, so the same batch can go
    // out more than once.
    id: generateRandomString(24),
    name,
    occurred_at: new Date().toISOString(),
    ...props,
  })

  if (queue.length >= MAX_BATCH) {
    flush()
    return
  }
  if (!flushTimer) flushTimer = setTimeout(() => flush(), FLUSH_INTERVAL_MS)
}

/**
 * Page views are keyed on the *named* route, never the path: a path carries
 * ids and slugs, and unbounded cardinality cannot be undone once the rollups
 * are built.
 */
export function trackPage(routeName: unknown) {
  if (typeof routeName !== 'string' || !routeName) return
  track(EventName.pageView, { route: routeName })
}

export function initAnalytics(router: Router) {
  if (!enabled() || started) return
  started = true

  // Before the first event, so app.open is already attributed. A tagged URL
  // opened while a session from an untagged visit is still alive rotates it
  // here, which is the whole point: the backend writes a session's attribution
  // once, so the campaign would otherwise land on nothing.
  noteLanding()

  track(EventName.appOpen, { properties: { platform: platform() } })
  trackPage(router.currentRoute.value.name)
  router.afterEach((to) => trackPage(to.name))

  // beforeunload/unload are unreliable on iOS Safari and inside a Capacitor
  // WebView; visibilitychange is the one that fires when an app is backgrounded.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true)
  })
  window.addEventListener('pagehide', () => flush(true))
}
